import { useIntl } from 'react-intl';

import { PERIOD_TYPE, ActiveStayPeriod } from 'src/features/stay/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { Separator } from 'src/registry/ui/Separator';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import CellStayPeriodZones from 'src/features/stay/components/cell/CellStayPeriodZones';

type StayPeriodsProps = {
    stayPeriodsData?: ActiveStayPeriod[];
};

const ActiveStayPeriods = ({ stayPeriodsData }: StayPeriodsProps) => {
    const { formatMessage, locale } = useIntl();
    const { getDurationFromStartDateToEndDate } = useGetStayDuration();

    const periodTypeTranslationMap = {
        [PERIOD_TYPE.STAY]: formatMessage(visitorMessages.stay),
        [PERIOD_TYPE.TRANSIT]: formatMessage(visitorMessages.transit),
    };

    return (
        <>
            {stayPeriodsData?.map((period) => {
                return (
                    <div className="mt-4 grid gap-2" key={period.id}>
                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.period)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.zones} fallbackMessage="-" className="text-sm">
                                {period?.zones?.length > 0 ? <CellStayPeriodZones zonesData={period?.zones} className="font-normal" /> : '-'}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.start)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.start} fallbackMessage="-" className="text-sm">
                                {getFormattedDateTimeForDetailPanel(period.start)}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.end)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.end} fallbackMessage="-" className="text-sm">
                                {getFormattedDateTimeForDetailPanel(period.end ?? undefined)}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.duration)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={period.duration}
                                fallbackMessage="-"
                                className="text-sm"
                            >
                                {getDurationFromStartDateToEndDate({
                                    startDate: period.start,
                                    endDate: period.end ?? undefined,
                                })}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.type)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.type} fallbackMessage="-" className="text-sm">
                                {period.type ? periodTypeTranslationMap[period.type as PERIOD_TYPE] : '-'}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>
                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.tariff)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody
                                control={period.pricing_plan?.translations}
                                fallbackMessage="-"
                                className="text-sm"
                            >
                                {period.pricing_plan?.translations?.find(
                                    (translation) => translation.language === locale,
                                )?.name ?? '-'}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <Separator />
                    </div>
                );
            })}

            {stayPeriodsData?.length === 0 && <div>{formatMessage(visitorMessages.noPeriods)}</div>}
        </>
    );
};

export default ActiveStayPeriods;
